import { useCallback, useEffect, useState } from 'react';
import {
  fetchAllProfiles,
  setUserVerified,
  updateUserProfile,
  deleteUser,
  type AdminProfile,
} from '@/lib/admin';
import { supabase } from '@/lib/supabase';

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-6">
      <h1 className="font-heading font-bold text-2xl text-foreground-50">{title}</h1>
      <p className="text-sm text-foreground-400 mt-1">{subtitle}</p>
    </div>
  );
}

interface EditForm {
  full_name: string;
  city: string;
  phone: string;
  role: string;
}

function UsersSection() {
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const [editing, setEditing] = useState<AdminProfile | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    full_name: '',
    city: '',
    phone: '',
    role: 'user',
  });
  const [deleting, setDeleting] = useState<AdminProfile | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllProfiles();
      setProfiles(data);
    } catch {
      setError('Impossible de charger les utilisateurs. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data?.session?.user?.id ?? null);
    });
  }, [load]);

  const toggleVerified = async (profile: AdminProfile) => {
    setBusyId(profile.id);
    setError('');
    setSuccess('');
    try {
      const next = !profile.verified;
      await setUserVerified(profile.id, next);
      setProfiles((prev) =>
        prev.map((p) => (p.id === profile.id ? { ...p, verified: next } : p)),
      );
      setSuccess(next ? 'Utilisateur certifié.' : 'Certification retirée.');
    } catch {
      setError("Impossible de mettre à jour la certification. Vérifiez que vous êtes bien administrateur.");
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (profile: AdminProfile) => {
    setEditing(profile);
    setEditForm({
      full_name: profile.full_name ?? '',
      city: profile.city ?? '',
      phone: profile.phone ?? '',
      role: profile.role ?? 'user',
    });
    setError('');
    setSuccess('');
  };

  const submitEdit = async () => {
    if (!editing) return;
    setBusyId(editing.id);
    setError('');
    setSuccess('');
    try {
      await updateUserProfile(editing.id, {
        full_name: editForm.full_name.trim() || null,
        city: editForm.city.trim() || null,
        phone: editForm.phone.trim() || null,
        role: editForm.role,
      });
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === editing.id
            ? {
                ...p,
                full_name: editForm.full_name.trim() || null,
                city: editForm.city.trim() || null,
                phone: editForm.phone.trim() || null,
                role: editForm.role,
              }
            : p,
        ),
      );
      setEditing(null);
      setSuccess('Utilisateur modifié avec succès.');
    } catch {
      setError("Impossible de modifier cet utilisateur. Vérifiez vos droits d'administrateur.");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    setError('');
    setSuccess('');
    try {
      await deleteUser(deleting.id);
      setProfiles((prev) => prev.filter((p) => p.id !== deleting.id));
      setDeleting(null);
      setSuccess('Utilisateur supprimé.');
    } catch {
      setError(
        "Impossible de supprimer cet utilisateur (il possède peut-être encore du contenu). Supprimez d'abord ses publications.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <SectionHeader
        title="Utilisateurs"
        subtitle="Gérer, modifier, certifier ou supprimer les comptes de la plateforme."
      />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20 mb-4">
          <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
          <p className="text-sm text-accent-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-500/10 border border-secondary-300/20 mb-4">
          <i className="ri-checkbox-circle-line text-secondary-300 mt-0.5 shrink-0" />
          <p className="text-sm text-secondary-300">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Chargement des utilisateurs…
        </div>
      ) : profiles.length === 0 ? (
        <p className="text-foreground-400">Aucun utilisateur trouvé.</p>
      ) : (
        <div className="bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 text-left text-foreground-400">
                  <th className="px-4 py-3 font-medium">Utilisateur</th>
                  <th className="px-4 py-3 font-medium">Rôle</th>
                  <th className="px-4 py-3 font-medium">Localisation</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-background-200/50 hover:bg-background-200/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10 text-xs font-semibold text-primary-400 shrink-0">
                          {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <span className="block font-medium text-foreground-50 truncate">
                            {u.full_name || 'Sans nom'}
                            {u.verified && (
                              <i
                                className="ri-verified-badge-fill text-accent-400 ml-1.5"
                                title="Utilisateur certifié"
                              />
                            )}
                            {u.id === currentUserId && (
                              <span className="ml-1.5 text-[10px] font-semibold text-primary-400 bg-primary-500/10 px-1.5 py-0.5 rounded-full">
                                vous
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-foreground-400 truncate">
                            {u.email}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary-500/20 text-secondary-300 whitespace-nowrap">
                        {u.role === 'admin' || u.role === 'super_admin'
                          ? 'Administrateur'
                          : u.role === 'moderator'
                            ? 'Modérateur'
                            : u.role || 'Membre'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">
                      {u.city || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {u.verified ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-accent-500/20 text-accent-300 whitespace-nowrap">
                          <i className="ri-verified-badge-fill" />
                          Certifié
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-background-200/60 text-foreground-500 whitespace-nowrap">
                          Non certifié
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => toggleVerified(u)}
                          disabled={busyId === u.id || u.role === 'admin'}
                          title={u.verified ? 'Décertifier' : 'Certifier'}
                          className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            u.verified
                              ? 'bg-accent-500/20 text-accent-300 hover:bg-accent-500/30'
                              : 'bg-primary-500/10 text-primary-400 hover:bg-primary-500/20'
                          }`}
                        >
                          {busyId === u.id ? (
                            <i className="ri-loader-4-line animate-spin text-sm" />
                          ) : (
                            <i className="ri-verified-badge-line text-sm" />
                          )}
                        </button>
                        <button
                          onClick={() => openEdit(u)}
                          title="Modifier"
                          className="w-8 h-8 flex items-center justify-center rounded-md bg-background-200/60 text-foreground-400 hover:text-foreground-50 hover:bg-background-300/60 transition-colors"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => setDeleting(u)}
                          disabled={u.id === currentUserId}
                          title={
                            u.id === currentUserId
                              ? 'Vous ne pouvez pas vous supprimer'
                              : 'Supprimer'
                          }
                          className="w-8 h-8 flex items-center justify-center rounded-md bg-foreground-800/80 text-foreground-100 hover:bg-foreground-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <i className="ri-delete-bin-6-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="w-full max-w-md bg-background-50 border border-background-200/70 rounded-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-semibold text-lg text-foreground-50">
                Modifier l'utilisateur
              </h2>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200/60 text-foreground-500 transition-colors"
                aria-label="Fermer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Nom complet
                </label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                    Téléphone
                  </label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                  Rôle
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                  disabled={editing.id === currentUserId}
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 focus:outline-none focus:border-primary-400 disabled:opacity-50"
                >
                  <option value="user">Membre</option>
                  <option value="moderator">Modérateur</option>
                  <option value="admin">Administrateur</option>
                </select>
                {editing.id === currentUserId && (
                  <p className="text-xs text-foreground-400 mt-1">
                    Vous ne pouvez pas modifier votre propre rôle.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-foreground-400 hover:text-foreground-50 hover:bg-background-200/60 transition-colors whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={submitEdit}
                disabled={busyId === editing.id}
                className="px-4 py-2 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                {busyId === editing.id ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-check-line" />
                )}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setDeleting(null)}
        >
          <div
            className="w-full max-w-sm bg-background-50 border border-background-200/70 rounded-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 mx-auto mb-4">
              <i className="ri-alert-line text-2xl text-accent-400" />
            </div>
            <h2 className="font-heading font-semibold text-lg text-foreground-50 text-center">
              Supprimer cet utilisateur ?
            </h2>
            <p className="text-sm text-foreground-400 text-center mt-2">
              « {deleting.full_name || deleting.email || 'Sans nom'} » sera définitivement supprimé.
              Cette action est irréversible.
            </p>
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-foreground-400 hover:text-foreground-50 hover:bg-background-200/60 transition-colors whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={busyId === deleting.id}
                className="px-4 py-2 rounded-md bg-foreground-800 text-foreground-100 text-sm font-semibold hover:bg-foreground-700 disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                {busyId === deleting.id ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-delete-bin-6-line" />
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersSection;